import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Trash2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import {
  FormInput,
  FormSelect,
  FormTextarea,
  PrimaryButton,
  SecondaryButton,
  Row,
} from "@/components/forms";
import { SkeletonLoader, useToast } from "@/components/system";
import { formatError } from "@/lib/format-error";

/**
 * /people/new + /people/:id — single component handles both. When the
 * id segment === "new" we skip the load and start with empty state.
 */

const RELATIONSHIP_KINDS: { value: string; label: string }[] = [
  { value: "family", label: "Family" },
  { value: "friend", label: "Friend" },
  { value: "colleague", label: "Colleague" },
  { value: "mentor", label: "Mentor" },
  { value: "mentee", label: "Mentee" },
  { value: "romantic", label: "Romantic" },
  { value: "professional", label: "Professional" },
  { value: "service_provider", label: "Service provider" },
  { value: "acquaintance", label: "Acquaintance" },
  { value: "group_member", label: "Group member" },
];

type PersonRow = {
  id: string;
  user_id: string;
  display_name: string;
  full_name: string | null;
  relationship_kind: string;
  relationship_specific: string | null;
  pronouns: string | null;
  language_preferred: string | null;
  time_zone: string | null;
  current_location: string | null;
  next_intended_contact_by: string | null;
  notes_jonas: string;
  contact: Record<string, string>;
  important_dates: Array<{ kind: string; date: string; notes?: string }>;
  updated_at: string;
};

type PersonForm = {
  display_name: string;
  full_name: string;
  relationship_kind: string;
  relationship_specific: string;
  pronouns: string;
  current_location: string;
  next_intended_contact_by: string;
  email: string;
  phone: string;
  notes_jonas: string;
};

const EMPTY: PersonForm = {
  display_name: "",
  full_name: "",
  relationship_kind: "friend",
  relationship_specific: "",
  pronouns: "",
  current_location: "",
  next_intended_contact_by: "",
  email: "",
  phone: "",
  notes_jonas: "",
};

export function PersonDetail() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { session } = useSession();
  const userId = session?.user?.id;

  const [form, setForm] = useState<PersonForm>(EMPTY);
  const [hydrated, setHydrated] = useState(isNew);

  const q = useQuery<PersonRow | null>({
    queryKey: ["person", id],
    enabled: Boolean(userId) && !isNew,
    staleTime: 30_000,
    queryFn: async () => {
      const client = supabase as unknown as {
        from: (t: string) => {
          select: (cols: string) => {
            eq: (
              col: string,
              v: string,
            ) => {
              maybeSingle: () => Promise<{ data: PersonRow | null; error: unknown }>;
            };
          };
        };
      };
      const { data, error } = await client.from("person").select("*").eq("id", id ?? "").maybeSingle();
      if (error) throw error as Error;
      return data;
    },
  });

  // Hydrate the form once when the row arrives.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (hydrated || !q.data) return;
    const p = q.data;
    setForm({
      display_name: p.display_name ?? "",
      full_name: p.full_name ?? "",
      relationship_kind: p.relationship_kind ?? "friend",
      relationship_specific: p.relationship_specific ?? "",
      pronouns: p.pronouns ?? "",
      current_location: p.current_location ?? "",
      next_intended_contact_by: p.next_intended_contact_by ?? "",
      email: p.contact?.email ?? "",
      phone: p.contact?.phone ?? "",
      notes_jonas: p.notes_jonas ?? "",
    });
    setHydrated(true);
  }, [q.data, hydrated]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const save = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error("Not signed in");
      const payload = {
        user_id: userId,
        display_name: form.display_name.trim(),
        full_name: form.full_name.trim() || null,
        relationship_kind: form.relationship_kind,
        relationship_specific: form.relationship_specific.trim() || null,
        pronouns: form.pronouns.trim() || null,
        current_location: form.current_location.trim() || null,
        next_intended_contact_by: form.next_intended_contact_by || null,
        contact: cleanContact({ email: form.email, phone: form.phone }),
        notes_jonas: form.notes_jonas,
      };
      const client = supabase as unknown as {
        from: (t: string) => {
          insert: (row: object) => {
            select: () => {
              single: () => Promise<{ data: PersonRow | null; error: unknown }>;
            };
          };
          update: (row: object) => {
            eq: (col: string, v: string) => {
              select: () => {
                single: () => Promise<{ data: PersonRow | null; error: unknown }>;
              };
            };
          };
        };
      };
      if (isNew) {
        const { data, error } = await client.from("person").insert(payload).select().single();
        if (error) throw error as Error;
        return data!;
      }
      const { data, error } = await client
        .from("person")
        .update(payload)
        .eq("id", id ?? "")
        .select()
        .single();
      if (error) throw error as Error;
      return data!;
    },
    onSuccess: (row) => {
      toast.show(isNew ? "Person added." : "Person updated.");
      void qc.invalidateQueries({ queryKey: ["people_list", userId] });
      if (isNew) navigate(`/people/${row.id}`, { replace: true });
      else void qc.invalidateQueries({ queryKey: ["person", id] });
    },
    onError: (e) => toast.show(formatError(e)),
  });

  const remove = useMutation({
    mutationFn: async () => {
      if (!id || isNew) return;
      const client = supabase as unknown as {
        from: (t: string) => {
          delete: () => { eq: (col: string, v: string) => Promise<{ error: unknown }> };
        };
      };
      const { error } = await client.from("person").delete().eq("id", id);
      if (error) throw error as Error;
    },
    onSuccess: () => {
      toast.show("Person deleted.");
      void qc.invalidateQueries({ queryKey: ["people_list", userId] });
      navigate("/people", { replace: true });
    },
    onError: (e) => toast.show(formatError(e)),
  });

  if (!isNew && q.isLoading) {
    return <SkeletonLoader variant="card" count={1} height={420} />;
  }
  if (!isNew && !q.data) {
    return (
      <article>
        <BackLink />
        <p style={{ color: "var(--fg-3)" }}>Person not found.</p>
      </article>
    );
  }

  return (
    <article aria-labelledby="person-heading">
      <BackLink />
      <h1
        id="person-heading"
        style={{
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: "var(--type-display-m)",
          margin: "var(--space-2) 0 var(--space-6)",
        }}
      >
        {isNew ? "Add a person." : form.display_name || "Edit person."}
      </h1>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!save.isPending && form.display_name.trim()) save.mutate();
        }}
        style={{ display: "grid", gap: "var(--space-4)", maxWidth: 720 }}
      >
        <Row>
          <FormInput
            label="Display name"
            value={form.display_name}
            onChange={(v) => setForm((s) => ({ ...s, display_name: v }))}
            required
            autoFocus
          />
          <FormInput
            label="Full name (optional)"
            value={form.full_name}
            onChange={(v) => setForm((s) => ({ ...s, full_name: v }))}
          />
        </Row>
        <Row>
          <FormSelect
            label="Relationship kind"
            value={form.relationship_kind}
            onChange={(v) => setForm((s) => ({ ...s, relationship_kind: v }))}
            options={RELATIONSHIP_KINDS}
          />
          <FormInput
            label="Specific (e.g. sister, PI)"
            value={form.relationship_specific}
            onChange={(v) => setForm((s) => ({ ...s, relationship_specific: v }))}
          />
        </Row>
        <Row>
          <FormInput
            label="Pronouns"
            value={form.pronouns}
            onChange={(v) => setForm((s) => ({ ...s, pronouns: v }))}
            placeholder="she/her, he/him, they/them"
          />
          <FormInput
            label="Current location"
            value={form.current_location}
            onChange={(v) => setForm((s) => ({ ...s, current_location: v }))}
            placeholder="Zurich"
          />
        </Row>
        <Row>
          <FormInput
            label="Email"
            type="email"
            value={form.email}
            onChange={(v) => setForm((s) => ({ ...s, email: v }))}
          />
          <FormInput
            label="Phone"
            type="tel"
            value={form.phone}
            onChange={(v) => setForm((s) => ({ ...s, phone: v }))}
          />
        </Row>
        <FormInput
          label="Next intended contact by (cadence)"
          type="date"
          value={form.next_intended_contact_by}
          onChange={(v) => setForm((s) => ({ ...s, next_intended_contact_by: v }))}
        />
        <FormTextarea
          label="Notes"
          value={form.notes_jonas}
          onChange={(v) => setForm((s) => ({ ...s, notes_jonas: v }))}
          rows={6}
          placeholder="What they're up to, conversation hooks, gift ideas…"
        />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "var(--space-3)",
            marginTop: "var(--space-2)",
          }}
        >
          {isNew ? (
            <span />
          ) : (
            <button
              type="button"
              onClick={() => {
                if (window.confirm(`Delete ${form.display_name}?`)) remove.mutate();
              }}
              disabled={remove.isPending}
              style={{
                background: "transparent",
                color: "var(--brick)",
                border: "1px solid var(--brick)",
                borderRadius: "var(--radius-card)",
                padding: "8px 14px",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: "var(--type-body-s)",
                cursor: "pointer",
              }}
            >
              <Trash2 size={14} strokeWidth={1.5} /> Delete
            </button>
          )}
          <div style={{ display: "flex", gap: "var(--space-3)" }}>
            <SecondaryButton onClick={() => navigate("/people")} disabled={save.isPending}>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              disabled={save.isPending || !form.display_name.trim()}
            >
              {save.isPending ? "Saving…" : isNew ? "Add person" : "Save changes"}
            </PrimaryButton>
          </div>
        </div>
      </form>
    </article>
  );
}

function BackLink() {
  return (
    <Link
      to="/people"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        color: "var(--fg-3)",
        fontSize: "var(--type-meta)",
        textDecoration: "none",
      }}
    >
      <ArrowLeft size={14} strokeWidth={1.5} /> People
    </Link>
  );
}

function cleanContact(input: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(input)) {
    const t = v.trim();
    if (t) out[k] = t;
  }
  return out;
}
